import React, { useCallback, useEffect, useState } from 'react';

import {
  atualizarMotivoDeParada,
  buscarMotivosDeParada,
  criarMotivoDeParada,
  desativarMotivoDeParada,
  reordenarMotivosDeParada,
  type MotivoDeParada,
} from '../../../api/motivosDeParadaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import TabelaSistema, { type ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../recepcao/pages/Recepcao.css';
import '../pages/css/adminSistema.css';

/**
 * Administração do Sistema → Oficina → Motivos de parada (at-134, ADR 0065).
 *
 * O catálogo da lista que o colaborador e o Chefe escolhem ao deixar o serviço de uma esteira
 * INACABADO. 🔴 O primeiro motivo ativo liga a exigência para a oficina inteira (bootstrap inerte
 * com a lista vazia) — a tela diz isso por extenso, porque é a consequência que o ADMIN não vê.
 *
 * ⚠️ Falha de carga não vira "nenhum motivo cadastrado": essa frase afirma que a escolha NÃO é
 * exigida, que é a leitura oposta se houver motivos (at-60).
 */
const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Para que serve',
    conteudo: (
      <p>
        Quando o serviço de uma esteira de trabalho fica <strong>inacabado</strong> (faltou peça, o
        cliente não autorizou, o teste reprovou), quem para o serviço escolhe o motivo nesta lista. É
        o que permite contar por que os serviços param, coisa que um texto livre não permite.
      </p>
    ),
  },
  {
    titulo: 'Onde a escolha aparece',
    conteudo: (
      <p>
        Em três lugares: no <strong>Finalizar atividade</strong> do colaborador, quando ele responde
        que o serviço ficou inacabado, e nos modais <strong>Alterar esteira</strong> e{' '}
        <strong>Aprovar esteira</strong> do Chefe de Oficina, quando ele deixa o serviço da esteira de
        origem inacabado. O motivo aparece depois no modal do veículo e na Auditoria.
      </p>
    ),
  },
  {
    titulo: 'Lista vazia x lista preenchida',
    conteudo: (
      <p>
        Enquanto não houver nenhum motivo ativo, a escolha <strong>não é exigida</strong>: o campo de
        texto livre de antes continua valendo. A partir do primeiro motivo ativo, ela passa a ser{' '}
        <strong>obrigatória</strong> nos três lugares, para toda a oficina.
      </p>
    ),
  },
  {
    titulo: 'Exige detalhe',
    conteudo: (
      <p>
        Marque <strong>Exige detalhe</strong> no motivo genérico ("Outro"): quem o escolher precisa
        descrever o que aconteceu. Nos demais, o detalhe é opcional.
      </p>
    ),
  },
  {
    titulo: 'Desativar não apaga',
    conteudo: (
      <p>
        Um motivo desativado some da lista de escolha, mas os serviços que já pararam por ele
        continuam mostrando o motivo. Para voltar a usá-lo, reative.
      </p>
    ),
  },
];

const MotivosDeParadaSection: React.FC = () => {
  const [motivos, setMotivos] = useState<MotivoDeParada[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [cargaFalhou, setCargaFalhou] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [novoExigeTexto, setNovoExigeTexto] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setMotivos(await buscarMotivosDeParada());
      setCargaFalhou(false);
    } catch (erro) {
      setMotivos([]);
      setCargaFalhou(true);
      notify.errorFrom(erro, 'Não foi possível carregar os motivos da parada. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const ativos = motivos.filter((m) => m.ativo);
  const inativos = motivos.filter((m) => !m.ativo);

  const executar = async (acao: () => Promise<unknown>, mensagemDeSucesso: string, mensagemDeErro: string) => {
    setSalvando(true);
    try {
      await acao();
      notify.success(mensagemDeSucesso);
      await carregar();
    } catch (erro) {
      notify.errorFrom(erro, mensagemDeErro);
    } finally {
      setSalvando(false);
    }
  };

  const handleCriar = () => {
    const nome = novoNome.trim();
    if (!nome) return;
    void executar(
      async () => {
        await criarMotivoDeParada({ nome, exigeTexto: novoExigeTexto });
        setNovoNome('');
        setNovoExigeTexto(false);
      },
      ativos.length === 0
        ? 'Motivo criado. A partir de agora, deixar um serviço inacabado exige escolher o motivo.'
        : 'Motivo criado.',
      'Não foi possível criar o motivo.',
    );
  };

  const handleMover = (index: number, direcao: -1 | 1) => {
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= ativos.length) return;
    const ids = ativos.map((m) => m.id);
    [ids[index], ids[alvo]] = [ids[alvo], ids[index]];
    void executar(() => reordenarMotivosDeParada(ids), 'Ordem atualizada.', 'Não foi possível alterar a ordem.');
  };

  const colunasAtivos: ColunaTabela<MotivoDeParada>[] = [
    {
      titulo: 'Ordem',
      largura: 90,
      texto: (m) => String(ativos.indexOf(m) + 1),
      render: (m) => {
        const index = ativos.indexOf(m);
        return (
          <div className="admin-reorder-group">
            <button
              className="admin-reorder-btn"
              title="Mover para cima"
              aria-label={`Mover ${m.nome} para cima`}
              disabled={salvando || index === 0}
              onClick={() => handleMover(index, -1)}
              type="button"
            >
              ▲
            </button>
            <button
              className="admin-reorder-btn"
              title="Mover para baixo"
              aria-label={`Mover ${m.nome} para baixo`}
              disabled={salvando || index === ativos.length - 1}
              onClick={() => handleMover(index, 1)}
              type="button"
            >
              ▼
            </button>
          </div>
        );
      },
    },
    { titulo: 'Motivo', largura: 260, larguraMaxima: 420, texto: (m) => m.nome },
    {
      titulo: 'Exige detalhe',
      largura: 130,
      texto: (m) => (m.exigeTexto ? 'Sim' : 'Não'),
      render: (m) => (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={m.exigeTexto}
            disabled={salvando}
            aria-label={`Exige detalhe em ${m.nome}`}
            onChange={(e) =>
              void executar(
                () => atualizarMotivoDeParada(m.id, { exigeTexto: e.target.checked }),
                'Motivo atualizado.',
                'Não foi possível atualizar o motivo.',
              )
            }
          />
          {m.exigeTexto ? 'Sim' : 'Não'}
        </label>
      ),
    },
    {
      titulo: 'Ações',
      largura: 120,
      texto: () => 'Desativar',
      render: (m) => (
        <button
          className="admin-btn admin-btn-danger-outline"
          disabled={salvando}
          onClick={() =>
            void executar(() => desativarMotivoDeParada(m.id), 'Motivo desativado.', 'Não foi possível desativar o motivo.')
          }
          type="button"
        >
          Desativar
        </button>
      ),
    },
  ];

  const colunasInativos: ColunaTabela<MotivoDeParada>[] = [
    { titulo: 'Motivo', largura: 260, larguraMaxima: 420, texto: (m) => m.nome },
    {
      titulo: 'Ações',
      largura: 120,
      texto: () => 'Reativar',
      render: (m) => (
        <button
          className="admin-btn admin-btn-success-outline"
          disabled={salvando}
          onClick={() =>
            void executar(
              () => atualizarMotivoDeParada(m.id, { ativo: true }),
              'Motivo reativado.',
              'Não foi possível reativar o motivo.',
            )
          }
          type="button"
        >
          Reativar
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Motivos de parada
          <button className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)} type="button">
            ?
          </button>
        </div>
        <p className="admin-subtitle">
          A lista que o colaborador e o Chefe escolhem ao deixar o serviço de uma esteira inacabado.
        </p>
      </div>

      {cargaFalhou && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar os motivos da parada. A lista abaixo pode estar incompleta, e não dá para saber se a
          escolha está sendo exigida. Recarregue a página antes de cadastrar.
        </div>
      )}

      {!carregando && !cargaFalhou && (
        <div className="aviso-de-tela">
          {ativos.length === 0
            ? 'Nenhum motivo ativo: a escolha NÃO é exigida, e quem deixa um serviço inacabado escreve o motivo em texto livre. O primeiro motivo criado aqui passa a exigir a escolha para toda a oficina.'
            : `${ativos.length} ${ativos.length === 1 ? 'motivo ativo' : 'motivos ativos'}: deixar um serviço inacabado exige escolher um deles.`}
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-title">Novo motivo</div>
        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="motivo-parada-nome">
              Nome
            </label>
            <input
              id="motivo-parada-nome"
              className="admin-input"
              value={novoNome}
              maxLength={80}
              placeholder="Ex: Aguardando peça"
              onChange={(e) => setNovoNome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCriar();
              }}
            />
          </div>
          {/* Caixa ao lado do texto e na altura do campo de nome, alinhada pela base do grid. */}
          <label
            htmlFor="motivo-parada-exige"
            className="admin-label"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 42,
              margin: 0,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            title="Quem escolher este motivo precisa escrever o detalhe (como em Outro)"
          >
            <input
              id="motivo-parada-exige"
              type="checkbox"
              checked={novoExigeTexto}
              onChange={(e) => setNovoExigeTexto(e.target.checked)}
            />
            Exige detalhe
          </label>
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleCriar}
            disabled={salvando || cargaFalhou || !novoNome.trim()}
            type="button"
          >
            Adicionar
          </button>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-title">Motivos ativos</div>
        <TabelaSistema
          colunas={colunasAtivos}
          itens={ativos}
          chave={(m) => m.id}
          carregando={carregando}
          vazio={cargaFalhou ? 'A lista não pôde ser carregada.' : 'Nenhum motivo ativo.'}
        />
      </div>

      {inativos.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-title">Desativados ({inativos.length})</div>
          <p className="admin-hint">
            Não aparecem na lista de escolha. Os serviços que já pararam por eles continuam mostrando o motivo.
          </p>
          <TabelaSistema colunas={colunasInativos} itens={inativos} chave={(m) => m.id} vazio="" />
        </div>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Motivos de parada"
        secoes={helpSections}
      />
    </div>
  );
};

export default MotivosDeParadaSection;
